import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { OarTaskService, StatementComparisonOptions } from '../oar-task-service';
import { BaseOarTaskService } from '../oar-task-service';
import { OarCacheService } from '../oar-cache-service';
import type { ClaimAnalysisResponse, ClaimAnalysisResult } from '../../types/claim-analysis';

/**
 * 带缓存的 OAR 任务服务
 *
 * 为权利要求分析功能提供缓存支持，显著提升重复请求的响应速度
 *
 * 特性：
 * - 缓存分析结果 15 分钟，避免重复的昂贵异步轮询
 * - 缓存命中时即时响应（<10ms）
 * - 保持与其他接口的完全兼容
 * - 支持 AbortSignal 取消操作
 * - 缓存不影响任务信息的获取
 *
 * 性能对比：
 * - 首次请求：15秒轮询（与原始实现相同）
 * - 缓存命中：即时响应
 * - 内存占用：极低（仅缓存分析结果）
 */
@injectable()
export class CachedOarTaskService implements OarTaskService {
    constructor(
        @inject(BaseOarTaskService)
        private readonly _baseService: OarTaskService,
        @inject(OarCacheService)
        private readonly _cache: OarCacheService,
    ) {}

    /**
     * 分析权利要求（带缓存）
     *
     * 缓存逻辑：
     * 1. 检查缓存中是否存在有效结果
     * 2. 缓存命中：直接返回缓存结果
     * 3. 缓存未命中：调用基础服务，缓存结果后返回
     *
     * @param taskId - 任务标识符
     * @param conclusiveOpinionId - 结论性意见 ID
     * @param signal - 可选的 AbortSignal，用于取消轮询
     * @returns 权利要求分析响应
     */
    async analyzeClaims(
        taskId: string,
        conclusiveOpinionId: string,
        signal?: AbortSignal,
    ): Promise<ClaimAnalysisResponse> {
        // 生成缓存键
        const cacheKey = this._generateCacheKey(taskId, conclusiveOpinionId);

        // 检查缓存
        const cachedResult = this._cache.get(cacheKey);
        if (cachedResult) {
            // 缓存命中，直接返回
            return cachedResult;
        }

        // 缓存未命中，调用基础服务
        const result = await this._baseService.analyzeClaims(
            taskId,
            conclusiveOpinionId,
            signal,
        );

        // 缓存结果（使用默认 TTL：15分钟）
        this._cache.set(cacheKey, result);

        return result;
    }

    /**
     * 生成陈述意见内容（无缓存）
     *
     * 此方法直接委托给基础服务，不使用缓存
     *
     * @param taskId - 任务标识符
     * @param claimAnalysisResults - 权利要求分析结果数组
     * @returns 生成的陈述意见内容
     */
    async generateStatementContent(
        taskId: string,
        claimAnalysisResults: ClaimAnalysisResult[],
    ): Promise<string> {
        return this._baseService.generateStatementContent(taskId, claimAnalysisResults);
    }

    /**
     * 生成陈述书对比内容（无缓存）
     *
     * 此方法直接委托给基础服务，不使用缓存
     *
     * @param taskId - 任务标识符
     * @param options - 对比选项
     * @returns 生成的对比内容
     */
    async generateStatementComparisonContent(
        taskId: string,
        options: StatementComparisonOptions,
    ): Promise<string> {
        return this._baseService.generateStatementComparisonContent(taskId, options);
    }

    /**
     * 生成缓存键
     *
     * @param taskId - 任务标识符
     * @param conclusiveOpinionId - 结论性意见 ID
     * @returns 缓存键，格式为 `${taskId}:${conclusiveOpinionId}`
     * @private
     */
    private _generateCacheKey(taskId: string, conclusiveOpinionId: string): string {
        return `${taskId}:${conclusiveOpinionId}`;
    }

    /**
     * 清除指定任务的缓存
     *
     * 当任务被修改或重新上传时，可以调用此方法清除相关缓存
     *
     * @param taskId - 任务标识符
     * @param conclusiveOpinionId - 可选的结论性意见 ID，如果不提供则清除该任务的所有缓存
     */
    clearTaskCache(taskId: string, conclusiveOpinionId?: string): void {
        if (conclusiveOpinionId !== undefined) {
            // 清除特定结论性意见的缓存
            const cacheKey = this._generateCacheKey(taskId, conclusiveOpinionId);
            this._cache.delete(cacheKey);
        } else {
            // 清除该任务的所有缓存
            // 注意：当前缓存服务接口不支持前缀查询，为了简化实现直接清空所有缓存
            // 在实际使用中，由于缓存项数量通常较少，这种简单实现是可接受的
            this._cache.clear();
        }
    }

    /**
     * 获取缓存统计信息
     *
     * @returns 缓存统计信息
     */
    getCacheStats(): { size: number; hasItems: boolean } {
        return {
            size: this._cache.size(),
            hasItems: this._cache.size() > 0,
        };
    }
}
